import { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Loader2, Search, Calendar, Lock, BadgeCheck, Check, Music, Plus, UserPlus, X, Sparkles } from 'lucide-react';

function formatReset(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', timeZone: 'America/New_York', timeZoneName: 'short' });
  } catch {
    return '';
  }
}

export function WeeklyLineupDialog({ open, onOpenChange, onSaved }) {
  const [artists, setArtists] = useState([]);
  // selections: Map<artistId, { customPrice: string }>
  const [selections, setSelections] = useState(new Map());
  // Guest artists already in this week's lineup — surfaced via /agent/lineup
  const [guests, setGuests] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nextReset, setNextReset] = useState(null);

  // Guest add form
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestPrice, setGuestPrice] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [avail, lineup] = await Promise.all([
        api.getAgentAvailableArtists(),
        api.getAgentLineup(),
      ]);
      const availableArtists = avail.data.artists || [];
      const lineupArtists = lineup.data.artists || [];
      setArtists(availableArtists);
      setNextReset(avail.data.next_reset);

      // Build selection map from current lineup (preserves custom_price overrides).
      const initial = new Map();
      for (const a of availableArtists) {
        if (a.is_in_my_lineup) {
          const override = a.custom_price != null ? String(a.custom_price) : '';
          initial.set(a.id, { customPrice: override });
        }
      }
      setSelections(initial);

      // Guest artists live only in /agent/lineup (not the available list).
      setGuests(lineupArtists.filter(a => a.is_guest));
    } catch {
      toast.error('Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id, locked) => {
    if (locked) return;
    setSelections(prev => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id); else next.set(id, { customPrice: '' });
      return next;
    });
  };

  const setOverridePrice = (id, value) => {
    setSelections(prev => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, { customPrice: value });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = [];
      // Existing site artists from the selection map
      for (const [id, { customPrice }] of selections.entries()) {
        const item = { artist_id: id };
        if (customPrice !== '' && customPrice != null) {
          const n = parseFloat(customPrice);
          if (!Number.isNaN(n) && n > 0) item.custom_price = n;
        }
        items.push(item);
      }
      // Keep guest artists in the lineup unconditionally — they were added explicitly
      // via "Add Guest Artist" and shouldn't get dropped when the agent edits selections.
      for (const g of guests) {
        items.push({ artist_id: g.id });
      }
      await api.setAgentLineup(items);
      toast.success(`Lineup saved (${items.length} artist${items.length === 1 ? '' : 's'}) — visible on Weekly Specials.`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save lineup');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGuest = async () => {
    const price = parseFloat(guestPrice);
    if (!guestName.trim()) { toast.error('Guest artist name is required'); return; }
    if (!Number.isFinite(price) || price <= 0) { toast.error('Enter a valid price'); return; }
    setAddingGuest(true);
    try {
      await api.addGuestArtist(guestName.trim(), price);
      toast.success(`Added ${guestName.trim()} to this week's lineup — pending admin review.`);
      setGuestName(''); setGuestPrice(''); setShowGuestForm(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add guest artist');
    } finally {
      setAddingGuest(false);
    }
  };

  const handleRemoveGuest = async (artistId) => {
    if (!window.confirm('Remove this guest artist from your lineup? This permanently deletes them.')) return;
    try {
      await api.removeGuestArtist(artistId);
      toast.success('Guest artist removed');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove guest artist');
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return artists;
    const q = search.toLowerCase();
    return artists.filter(a => a.name.toLowerCase().includes(q) || (a.bio || '').toLowerCase().includes(q));
  }, [artists, search]);

  const totalSelected = selections.size + guests.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-surface border-[#262626] max-h-[88vh] overflow-hidden flex flex-col" data-testid="weekly-lineup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-lime" /> This Week&apos;s Lineup
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Tick artists, optionally override their Weekly Specials price, or add a new guest artist.
            {nextReset && (
              <span className="block mt-1 text-xs text-[#666]">
                Resets {formatReset(nextReset)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Guest artists already in lineup */}
        {guests.length > 0 && (
          <div className="border border-lime/30 bg-lime/5 rounded-xl p-3" data-testid="lineup-guests-section">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide text-lime font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Guest Artists (pending admin review)
            </div>
            <ul className="space-y-2">
              {guests.map(g => (
                <li key={g.id} className="flex items-center gap-3 text-sm" data-testid={`guest-row-${g.id}`}>
                  <span className="font-medium truncate flex-1">{g.name}</span>
                  <span className="text-xs text-[#A1A1AA]">${g.price?.toFixed?.(2)}</span>
                  <button
                    onClick={() => handleRemoveGuest(g.id)}
                    className="text-red-400 hover:text-red-300"
                    title="Remove guest"
                    data-testid={`remove-guest-${g.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Add Guest Artist row */}
        {showGuestForm ? (
          <div className="border border-lime/40 bg-lime/5 rounded-xl p-3 flex items-center gap-2" data-testid="add-guest-form">
            <UserPlus className="w-4 h-4 text-lime flex-shrink-0" />
            <Input
              placeholder="Artist name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="input-dark h-9 flex-1"
              data-testid="guest-name-input"
              maxLength={100}
            />
            <Input
              placeholder="Price"
              type="number"
              value={guestPrice}
              onChange={(e) => setGuestPrice(e.target.value)}
              className="input-dark h-9 w-24"
              data-testid="guest-price-input"
              min={1}
              step={1}
            />
            <Button
              size="sm"
              className="btn-primary h-9"
              onClick={handleAddGuest}
              disabled={addingGuest}
              data-testid="guest-add-btn"
            >
              {addingGuest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowGuestForm(false); setGuestName(''); setGuestPrice(''); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="border-lime/30 text-lime hover:bg-lime/10 self-start"
            onClick={() => setShowGuestForm(true)}
            data-testid="show-guest-form-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Guest Artist
          </Button>
        )}

        <div className="relative my-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
          <Input
            placeholder="Search artists…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-dark pl-10"
            data-testid="lineup-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="lineup-artist-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-lime" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-[#A1A1AA]">
              <Music className="w-10 h-10 mx-auto mb-3 text-[#333]" />
              No artists match your search.
            </div>
          ) : (
            <ul className="divide-y divide-[#262626] border border-[#262626] rounded-xl overflow-hidden">
              {filtered.map((a) => {
                const selection = selections.get(a.id);
                const checked = !!selection;
                const locked = a.is_locked;
                return (
                  <li
                    key={a.id}
                    className={`flex items-center gap-3 p-3 transition-colors ${locked ? 'opacity-50' : 'hover:bg-surface-highlight'}`}
                    data-testid={`lineup-row-${a.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(a.id, locked)}
                      disabled={locked}
                      className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-lime border-lime' : 'border-[#444]'}`}
                      data-testid={`lineup-toggle-${a.id}`}
                      aria-label={`Toggle ${a.name}`}
                    >
                      {checked && <Check className="w-4 h-4 text-black" />}
                    </button>
                    <img src={a.image_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggle(a.id, locked)}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{a.name}</span>
                        {a.is_verified && <BadgeCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                      </div>
                      <div className="text-xs text-[#A1A1AA] truncate">
                        Normal price ${a.price?.toFixed?.(2)} · {a.delivery_days}d
                      </div>
                    </div>
                    {checked && !locked && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-[#A1A1AA]">$</span>
                        <Input
                          type="number"
                          placeholder={a.price?.toFixed?.(2)}
                          value={selection.customPrice}
                          onChange={(e) => setOverridePrice(a.id, e.target.value)}
                          className="input-dark h-8 w-24 text-sm"
                          min={1}
                          step={1}
                          data-testid={`lineup-price-input-${a.id}`}
                          title="Weekly Specials price (leave blank to use the artist's normal price)"
                        />
                      </div>
                    )}
                    {locked && (
                      <Badge className="bg-yellow-500/10 text-yellow-400 border-0 flex-shrink-0">
                        <Lock className="w-3 h-3 mr-1" /> Claimed
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="mt-3 flex-shrink-0">
          <div className="text-sm text-[#A1A1AA] flex-1">
            {totalSelected} artist{totalSelected === 1 ? '' : 's'} in lineup
          </div>
          <Button variant="outline" className="border-[#262626]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="lineup-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Lineup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
